import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';

/** Valor canônico quando o paciente marca “Não tenho/Nenhum”. */
export const CONDITIONAL_NONE_VALUE = 'Nenhum';

export function isConditionalNoneValue(value: string | null | undefined): boolean {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return (
    normalized === '' ||
    normalized === 'nenhum' ||
    normalized === 'nenhuma' ||
    normalized === 'nao tenho' ||
    normalized === 'não tenho' ||
    normalized === 'n/a' ||
    normalized === 'na'
  );
}

export interface ConditionalTextareaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  noInfoLabel?: string;
  hasInfoLabel?: string;
}

export function ConditionalTextarea({
  label,
  value,
  onChange,
  error,
  required,
  placeholder = 'Descreva detalhadamente...',
  className,
  id,
  noInfoLabel = 'Não tenho/Nenhum',
  hasInfoLabel = 'Tenho',
}: ConditionalTextareaProps) {
  const [selection, setSelection] = useState<'HAS_INFO' | 'NO_INFO'>(() =>
    value.trim() !== '' && !isConditionalNoneValue(value) ? 'HAS_INFO' : 'NO_INFO',
  );
  const syncedNoneRef = useRef(false);

  // Mantém a seleção coerente com o valor (sem abrir textarea para "Nenhum").
  useEffect(() => {
    if (value.trim() !== '' && !isConditionalNoneValue(value)) {
      setSelection('HAS_INFO');
      return;
    }
    setSelection('NO_INFO');
  }, [value]);

  // Default visual “Nenhum” → grava valor canônico para a validação do checklist aceitar.
  useEffect(() => {
    if (syncedNoneRef.current) return;
    if (selection === 'NO_INFO' && value.trim() === '') {
      syncedNoneRef.current = true;
      onChange(CONDITIONAL_NONE_VALUE);
    }
  }, [selection, value, onChange]);

  const handleSelection = (newSelection: 'HAS_INFO' | 'NO_INFO') => {
    setSelection(newSelection);
    if (newSelection === 'NO_INFO') {
      onChange(CONDITIONAL_NONE_VALUE);
      return;
    }
    // “Tenho”: limpa o marcador Nenhum e exige texto real.
    if (isConditionalNoneValue(value)) {
      onChange('');
    }
  };

  const showTextarea = selection === 'HAS_INFO';

  return (
    <div className={cn('space-y-3', className)}>
      <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>

      <div className="flex gap-4">
        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
          <input
            type="radio"
            name={id || label}
            value="NO_INFO"
            checked={selection === 'NO_INFO'}
            onChange={() => handleSelection('NO_INFO')}
            className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
          />
          {noInfoLabel}
        </label>

        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
          <input
            type="radio"
            name={id || label}
            value="HAS_INFO"
            checked={selection === 'HAS_INFO'}
            onChange={() => handleSelection('HAS_INFO')}
            className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
          />
          {hasInfoLabel}
        </label>
      </div>

      {showTextarea && (
        <div className="mt-2 animate-in fade-in slide-in-from-top-1 duration-200">
          <textarea
            autoFocus
            required={required}
            value={isConditionalNoneValue(value) ? '' : value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={3}
            className={cn(
              'w-full rounded-md border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-neutral-900 resize-none transition-colors',
              error
                ? 'border-red-500 dark:border-red-500/50'
                : 'border-gray-200 dark:border-neutral-700',
            )}
          />
        </div>
      )}

      {error && <span className="block text-xs text-red-600">{error}</span>}
    </div>
  );
}
